import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppState } from "../store";
import { onGet, onPost, onPatch, onDelete } from "../../core/api-service";
import { ErrorState, ResponseState } from "../common-state";
import paths from "../../navigation/paths";
import { setErrorState } from "./common-slice";

export enum JDStatus {
  Open = "open",
  Closed = "closed",
  Draft = "draft",
}

export enum JobType {
  FullTime = "full_time",
  PartTime = "part_time",
  Contract = "contract",
  Internship = "internship",
}

export interface JDResponse {
  id: string;
  title: string;
  description: string;
  deadline: string;
  requirements: string;
  status: JDStatus;
  jobType: JobType;
  location: string;
  createdAt: string;
}

export interface JDCreateRequest {
  title: string;
  description?: string;
  deadline?: string;
  requirements?: string;
  status: JDStatus;
  jobType: JobType;
  location?: string;
}

export interface JDUpdateRequest extends JDCreateRequest {
  id: string;
}

export interface GetJDByIdRequest {
  id: string;
}

export interface GetAllJDsRequest {
  status?: JDStatus[];
  jobType?: JobType[];
}

interface JDState {
  createJDState: { data: ResponseState<JDResponse> | null; loading: boolean };
  jdByIdState: { data: JDResponse | null; loading: boolean };
  allJDsState: { data: JDResponse[]; loading: boolean };
  updateJDState: { data: ResponseState<void> | null; loading: boolean };
  deleteJDState: { data: ResponseState<void> | null; loading: boolean };
}

export const createJDStateAction = createAsyncThunk(
  "jds/create",
  async (params: JDCreateRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/jds", params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const getJDByIdStateAction = createAsyncThunk(
  "jds/id",
  async (params: GetJDByIdRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onGet(`/jds/${params.id}`);
      return response.data;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Job description does not exist",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const allJDsStateAction = createAsyncThunk(
  "jds/all",
  async (params: GetAllJDsRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPost("/jds/all", params);
      return response.data;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Error occurred! Please try again.",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const updateJDStateAction = createAsyncThunk(
  "jds/update",
  async (params: JDUpdateRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onPatch(`/jds/${params.id}`, params);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Job description does not exist",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const deleteJDStateAction = createAsyncThunk(
  "jds/delete",
  async (params: GetJDByIdRequest, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setErrorState(null));
      const response = await onDelete(`/jds/${params.id}`);
      return response;
    } catch (error: any) {
      const errorState: ErrorState = {
        message:
          error.response?.data?.message ?? "Job description does not exist",
        redirect:
          error.response?.status === 401
            ? {
                destination: paths.UNAUTHORIZED,
                permanent: true,
              }
            : undefined,
      };
      dispatch(setErrorState(errorState));
      return rejectWithValue(errorState);
    }
  }
);

export const jdSlice = createSlice({
  name: "jds",
  initialState: {
    createJDState: { data: null, loading: false },
    jdByIdState: { data: null, loading: false },
    allJDsState: { data: [], loading: false },
    updateJDState: { data: null, loading: false },
    deleteJDState: { data: null, loading: false },
  } as JDState,
  reducers: {
    clearJDStates(state, _: PayloadAction) {
      state.createJDState = { data: null, loading: false };
      state.jdByIdState = { data: null, loading: false };
      state.allJDsState = { data: [], loading: false };
      state.updateJDState = { data: null, loading: false };
      state.deleteJDState = { data: null, loading: false };
    },
    clearCreateJDState(state, _: PayloadAction) {
      state.createJDState = { data: null, loading: false };
    },
    clearUpdateJDState(state, _: PayloadAction) {
      state.updateJDState = { data: null, loading: false };
    },
    clearDeleteJDState(state, _: PayloadAction) {
      state.deleteJDState = { data: null, loading: false };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createJDStateAction.pending, (state: JDState, _) => {
        state.createJDState.loading = true;
        state.createJDState.data = null;
      })
      .addCase(createJDStateAction.fulfilled, (state: JDState, action) => {
        state.createJDState.loading = false;
        state.createJDState.data = action.payload as ResponseState<JDResponse>;
      })
      .addCase(createJDStateAction.rejected, (state: JDState, _) => {
        state.createJDState.loading = false;
      })
      .addCase(getJDByIdStateAction.pending, (state: JDState, _) => {
        state.jdByIdState.loading = true;
        state.jdByIdState.data = null;
      })
      .addCase(getJDByIdStateAction.fulfilled, (state: JDState, action) => {
        state.jdByIdState.loading = false;
        state.jdByIdState.data = action.payload as JDResponse;
      })
      .addCase(getJDByIdStateAction.rejected, (state: JDState, _) => {
        state.jdByIdState.loading = false;
      })
      .addCase(allJDsStateAction.pending, (state: JDState, _) => {
        state.allJDsState.loading = true;
        state.allJDsState.data = [];
      })
      .addCase(allJDsStateAction.fulfilled, (state: JDState, action) => {
        state.allJDsState.loading = false;
        state.allJDsState.data = action.payload as JDResponse[];
      })
      .addCase(allJDsStateAction.rejected, (state: JDState, _) => {
        state.allJDsState.loading = false;
      })
      .addCase(updateJDStateAction.pending, (state: JDState, _) => {
        state.updateJDState.loading = true;
        state.updateJDState.data = null;
      })
      .addCase(updateJDStateAction.fulfilled, (state: JDState, action) => {
        state.updateJDState.loading = false;
        state.updateJDState.data = action.payload as ResponseState<void>;
      })
      .addCase(updateJDStateAction.rejected, (state: JDState, _) => {
        state.updateJDState.loading = false;
      })
      .addCase(deleteJDStateAction.pending, (state: JDState, _) => {
        state.deleteJDState.loading = true;
        state.deleteJDState.data = null;
      })
      .addCase(deleteJDStateAction.fulfilled, (state: JDState, action) => {
        state.deleteJDState.loading = false;
        state.deleteJDState.data = action.payload as ResponseState<void>;
      })
      .addCase(deleteJDStateAction.rejected, (state: JDState, _) => {
        state.deleteJDState.loading = false;
      });
  },
});

export const {
  clearJDStates,
  clearCreateJDState,
  clearUpdateJDState,
  clearDeleteJDState,
} = jdSlice.actions;

export const getCreateJDState = (state: AppState) => state.jds.createJDState;
export const getJDByIdState = (state: AppState) => state.jds.jdByIdState;
export const getAllJDsState = (state: AppState) => state.jds.allJDsState;
export const getUpdateJDState = (state: AppState) => state.jds.updateJDState;
export const getDeleteJDState = (state: AppState) => state.jds.deleteJDState;

export default jdSlice.reducer;
